import React, { useEffect, useState } from "react";
import Navbar from "./Navbar";
import axiosURLS from "../services/Api";
import PhoneInput from "react-phone-input-2";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

type FilterType = "upcoming" | "past" | "all";

function Status() {
  const [status, setStatus] = useState<any>([]);
  const [mobileNumber, setMobileNumber] = useState("");
  const [userFriendlyId, setUserFriendlyId] = useState("");
  const [searchCriteria, setSearchCriteria] = useState({ mobileNumber: "", userFriendlyId: "" });
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterType>("upcoming");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    if (searchCriteria.mobileNumber || searchCriteria.userFriendlyId) {
      setLoading(true);
      axiosURLS.post("/onlineBooking/search", {
        mobileNumber: mobileNumber ? "+" + searchCriteria.mobileNumber : "",
        userFriendlyId: searchCriteria.userFriendlyId,
      }).then((response) => { setStatus(response.data); setLoading(false); })
        .catch((error) => { console.log("Error fetching availability data:", error); setLoading(false); });
    }
  }, [searchCriteria]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!mobileNumber && !userFriendlyId) { toast.error("Please fill Booking Id or Mobile Number"); return; }
    setSearchCriteria({ mobileNumber, userFriendlyId });
  };

  const handleMobileFocus = () => setUserFriendlyId("");
  const handleBookingIdFocus = () => setMobileNumber("");

  const formatTime = (date: Date) => {
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12; hours = hours ? hours : 12;
    return hours + ":" + (minutes < 10 ? "0" + minutes : minutes) + " " + ampm;
  };

  const formatDuration = (minutes: number) => {
    if (!minutes) return null;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (m === 0) return `${h} hr${h !== 1 ? "s" : ""}`;
    return `${h}h ${m}m`;
  };

  const handleShare = (booking: any) => {
    const bookingDate = new Date(booking.bookingTime);
    const text = `My Super Sixes booking:\nID: ${booking.userFriendlyBookingId}\nDate: ${bookingDate.toDateString()}\nTime: ${formatTime(bookingDate)}\nArena: ${booking.arenaRef?.name || "Cricket Bay"}`;
    if (navigator.share) {
      navigator.share({ title: "Super Sixes Booking", text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text).then(() => toast.success("Booking details copied!")).catch(() => toast.error("Could not copy details"));
    }
  };

  const getFilteredBookings = () => {
    if (!Array.isArray(status) || status.length === 0) return [];
    const now = new Date();
    return status.filter((b: any) => {
      const bd = new Date(b.bookingTime);
      if (activeFilter === "upcoming") return bd >= now;
      if (activeFilter === "past") return bd < now;
      return true;
    }).sort((a: any, b: any) => new Date(a.bookingTime).getTime() - new Date(b.bookingTime).getTime());
  };

  const renderBookingCard = (booking: any) => {
    const bookingDate = new Date(booking.bookingTime);
    const now = new Date();
    const isUpcoming = bookingDate >= now;
    const bs = (booking.bookingStatus || "").toUpperCase();
    const obs = (booking.onlineBookingStatus || "").toUpperCase();
    const isConfirmed = obs === "BOOKED" || bs === "CONFIRMED";
    const isExpanded = expandedId === booking.id;

    return (
      <div key={booking.id} className="rounded-2xl transition-all active:scale-[0.98]"
        style={{ background: isUpcoming ? "#1e2333" : "#1a1f29", border: isUpcoming ? "1.5px solid rgba(255,90,34,0.22)" : "1.5px solid rgba(255,255,255,0.06)", borderTop: isUpcoming ? "1px solid rgba(255,90,34,0.30)" : "1px solid rgba(255,255,255,0.08)", boxShadow: "0 1px 3px rgba(0,0,0,0.50), 0 6px 18px rgba(0,0,0,0.40), 0 16px 40px rgba(0,0,0,0.25)", overflow: "hidden" }}>

        {/* Card main content */}
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ background: isConfirmed ? "rgba(16,185,129,0.2)" : "rgba(245,158,11,0.2)", color: isConfirmed ? "#10b981" : "#f59e0b" }}>
              {isConfirmed
                ? <svg width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                : <svg width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
              {isConfirmed ? "Confirmed" : "Pending"}
            </span>
            <span className="text-xs" style={{ fontFamily: "'Courier New',monospace", color: "#6b7280" }}>{booking.userFriendlyBookingId}</span>
          </div>
          <div className="mb-3">
            <div className="flex items-baseline gap-2 mb-1">
              <span style={{ fontFamily: "'Impact','Arial Black',sans-serif", fontSize: 36, fontWeight: 900, color: "#ffffff", lineHeight: 1 }}>{bookingDate.getDate()}</span>
              <span style={{ fontSize: 16, color: "#a0aec0" }}>{bookingDate.toLocaleString("default",{month:"short"})} {bookingDate.getFullYear()}</span>
            </div>
            <div style={{ fontFamily: "'Courier New',monospace", fontSize: 16, color: "#FF5A22", fontWeight: 600 }}>{formatTime(bookingDate)}</div>
          </div>
          <div className="flex items-center gap-2 mb-4" style={{ color: "#a0aec0", fontSize: 14 }}>
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
            <span>{booking.arenaRef?.name || "Cricket Bay"}</span>
          </div>
          <div className="flex gap-2 pt-4" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
            <button
              className="flex-1 h-10 rounded-xl text-sm font-semibold text-white transition-all"
              style={{ background: isExpanded ? "rgba(255,90,34,0.15)" : "#252d3a", border: isExpanded ? "1.5px solid rgba(255,90,34,0.4)" : "1.5px solid rgba(255,255,255,0.15)", color: isExpanded ? "#FF5A22" : "#ffffff" }}
              onClick={() => setExpandedId(isExpanded ? null : booking.id)}
            >
              {isExpanded ? "Hide Details" : "View Details"}
            </button>
            <button
              onClick={() => handleShare(booking)}
              className="w-10 h-10 rounded-xl flex items-center justify-center transition-all active:scale-90"
              style={{ background: "transparent", border: "1.5px solid rgba(255,255,255,0.15)", color: "#a0aec0" }}
              title="Share booking"
            >
              <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/></svg>
            </button>
          </div>
        </div>

        {/* Expanded details accordion */}
        {isExpanded && (
          <div className="px-5 pb-5" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="rounded-xl p-4 mt-4 space-y-3" style={{ background: "#252d3a" }}>
              {booking.firstName && (
                <div className="flex justify-between items-center">
                  <span style={{ color: "#6b7280", fontSize: 13 }}>Name</span>
                  <span style={{ color: "#ffffff", fontSize: 13, fontWeight: 600 }}>{booking.firstName}</span>
                </div>
              )}
              {booking.contactDetail?.mobileNumber && (
                <>
                  <div style={{ height: 1, background: "rgba(255,255,255,0.06)" }} />
                  <div className="flex justify-between items-center">
                    <span style={{ color: "#6b7280", fontSize: 13 }}>Mobile</span>
                    <span style={{ color: "#ffffff", fontSize: 13, fontFamily: "'Courier New',monospace" }}>{booking.contactDetail.mobileNumber}</span>
                  </div>
                </>
              )}
              {booking.bookedForMinutes > 0 && (
                <>
                  <div style={{ height: 1, background: "rgba(255,255,255,0.06)" }} />
                  <div className="flex justify-between items-center">
                    <span style={{ color: "#6b7280", fontSize: 13 }}>Duration</span>
                    <span style={{ color: "#FF5A22", fontSize: 13, fontWeight: 600 }}>{formatDuration(booking.bookedForMinutes)}</span>
                  </div>
                </>
              )}
              {booking.contactDetail?.email && (
                <>
                  <div style={{ height: 1, background: "rgba(255,255,255,0.06)" }} />
                  <div className="flex justify-between items-center">
                    <span style={{ color: "#6b7280", fontSize: 13 }}>Email</span>
                    <span style={{ color: "#a0aec0", fontSize: 13 }}>{booking.contactDetail.email}</span>
                  </div>
                </>
              )}
              <div style={{ height: 1, background: "rgba(255,255,255,0.06)" }} />
              <div className="flex justify-between items-center">
                <span style={{ color: "#6b7280", fontSize: 13 }}>Booking ID</span>
                <span style={{ color: "#ffffff", fontSize: 13, fontFamily: "'Courier New',monospace" }}>{booking.userFriendlyBookingId}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBookings = () => {
    if (loading) return (
      <div className="flex flex-col gap-4">
        {[0,1,2].map(i => <SkeletonTheme key={i} baseColor="#252d3a" highlightColor="#1a1f29"><Skeleton height={200} borderRadius={16}/></SkeletonTheme>)}
      </div>
    );
    if (!searchCriteria.mobileNumber && !searchCriteria.userFriendlyId) return null;
    const filtered = getFilteredBookings();
    if (filtered.length === 0) return (
      <div className="flex flex-col items-center py-16 text-center">
        <svg width="64" height="64" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth={1} className="mb-4 opacity-50"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
        <p style={{ color: "#a0aec0", fontSize: 18 }}>No bookings found</p>
      </div>
    );
    return <div className="flex flex-col gap-4">{filtered.map(renderBookingCard)}</div>;
  };

  return (
    <>
      <Navbar />
      <motion.div className="pt-20 px-5 pb-10 min-h-screen"
        style={{ background: "linear-gradient(180deg,#0f1419 0%,#1a1f29 100%)" }}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <div className="max-w-lg mx-auto">
          <h1 className="mb-6 mt-4" style={{ fontFamily: "'Impact','Arial Black',sans-serif", fontSize: 28, fontWeight: 900, color: "#ffffff" }}>My Bookings</h1>
          <form onSubmit={handleSubmit} className="mb-8">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: "#a0aec0" }}>Booking ID</label>
              <div className="relative">
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                <input type="text" value={userFriendlyId} onFocus={handleBookingIdFocus}
                  onChange={(e) => setUserFriendlyId(e.target.value.substring(0,15).toUpperCase())}
                  placeholder="e.g. LKO2FEB21A0"
                  className="w-full h-14 pl-12 pr-4 text-white placeholder-[#6b7280] rounded-2xl outline-none"
                  style={{ background: "#1a1f29", border: "1.5px solid rgba(255,255,255,0.1)", fontSize: 16 }}/>
              </div>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.1)" }}/>
              <span className="text-sm font-semibold" style={{ color: "#6b7280" }}>Or</span>
              <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.1)" }}/>
            </div>
            <div className="mb-5">
              <label className="block text-sm font-medium mb-2" style={{ color: "#a0aec0" }}>Mobile Number</label>
              <PhoneInput country={"in"} value={mobileNumber} onFocus={handleMobileFocus} placeholder="Enter your mobile number" onChange={(val) => setMobileNumber(val.substring(0,15))}/>
            </div>
            <div className="flex justify-center">
              <button type="submit" className="px-10 h-12 rounded-2xl font-bold text-base transition-all active:scale-95"
                style={{ background: "linear-gradient(135deg,#FF5A22 0%,#FF8B5E 100%)", color: "#0f1419", boxShadow: "0 6px 20px rgba(255,90,34,0.3)" }}>
                SEARCH
              </button>
            </div>
          </form>
          {Array.isArray(status) && status.length > 0 && (
            <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
              {(["upcoming","past","all"] as FilterType[]).map(f => (
                <button key={f} onClick={() => setActiveFilter(f)}
                  className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all"
                  style={{ background: activeFilter===f ? "rgba(45,122,78,0.2)" : "#1a1f29", border: activeFilter===f ? "1.5px solid #2d7a4e" : "1.5px solid rgba(255,255,255,0.1)", color: activeFilter===f ? "#2d7a4e" : "#a0aec0" }}>
                  {f.charAt(0).toUpperCase()+f.slice(1)}
                </button>
              ))}
            </div>
          )}
          {renderBookings()}
        </div>
      </motion.div>
    </>
  );
}

export default Status;
